import {
  FC,
  MouseEvent,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarDays } from "lucide-react";
import type { Appointment } from "@myapp/contract";
import Modal from "../../../shared/ui/overlay/Modal/Modal";
import { TechnicianFeedbackSummary } from "../../../shared/ui";
import { ScheduleConflictDialogs } from "../../../shared/ui/feedback/ScheduleConflictDialogs/ScheduleConflictDialogs";
import { DiscardChangesDialog } from "../../../shared/ui/feedback/DiscardChangesDialog/DiscardChangesDialog";
import { useScheduleConflictFlow } from "../../../shared/hooks/useScheduleConflictFlow";
import { sameSlot, type ScheduleOverrides, type ScheduleSlot } from "../../../shared/lib/schedule";
import { routes } from "../../../shared/config/routes";
import { getErrorMessage } from "../../../shared/helpers/getErrorMessage";
import {
  appointmentApi,
  useAppointmentDetail,
  useCreateAppointment,
  useUpdateAppointment,
} from "../../../entities/appointment";
import { useTechnicianShift } from "../../../entities/technician-profile";
import {
  AppointmentFormValues,
  appointmentFormSchema,
  applySlot,
  fromAppointment,
  getDefaultValues,
  hasActuals,
  toCreateDto,
  toSlot,
  toUpdateDto,
} from "../model/appointmentForm.schema";
import { useUpsertAppointment } from "../model/useUpsertAppointmentParams";
import { useFullDaySync } from "../model/useFullDaySync";
import { useActualEndSync } from "../model/useActualEndSync";
import { AppointmentFormFields } from "./AppointmentFormFields";

/**
 * Create / edit an appointment, driven entirely by URL state.
 *
 * ONE ROUTE FOR BOTH MODES OF CREATE: `POST /appointments` makes a single appointment when
 * `recurrence` is absent and a whole series when it is present — the backend expands the rule,
 * reserves every occurrence and mints the `seriesId`. A single conflict rejects the whole series
 * and writes nothing, which is why there is no partial-success path to handle here.
 *
 * CONFLICTS: both routes answer 409 in three flavours, and the API checks them in this order.
 * `SCHEDULE_COMPLETED_BLOCKERS` is completed work in the range and is never overridable, so it
 * gets a dialog with no confirm. `SCHEDULE_OUTSIDE_WORKING_HOURS` means the technician does not
 * work then — nothing is in the way — and is waived with `allowOutsideWorkingHours: true`.
 * `SCHEDULE_CONFLICT` carries the overlapping entries and is waived with `force: true`, which
 * double-books the technician and flags the displaced entries for Umdisponierung.
 *
 * The last two can fire in sequence on the same slot, so the overrides accumulate across retries.
 * All of that lives in `useScheduleConflictFlow` — this file only says what to resend and how the
 * dialogs are worded. `features/task-upsert` uses the same pair.
 *
 * Owner route, not `/schedule`: a form changes time *and* business fields, and `/schedule` silently
 * drops everything outside its nine scheduling fields. See `apps/web/CLAUDE.md`.
 *
 * GANZTÄGIG is derived here, not by the API — `allDay` is a passenger flag server-side, worth two
 * skipped checks and no arithmetic at all. `useFullDaySync` reshapes the Termin to fill the
 * technician's shift, and unticks the box again the moment anything reshapes it back. That belongs
 * on this side because the form is the single owner of what the board draws (board README §4a): a
 * server-side derivation would leave the live block showing the old durations until the refetch.
 *
 * TWO HOMES, ONE HEADER: the same modal opens from `pages/work-order-detail` (which passes
 * `workOrderId`) and from `widgets/disposition-board` (which does not — the id arrives through the
 * URL or the edited appointment). The header names the Auftrag in both, and carries the link to
 * whichever of the two the user is *not* on: "Kalender" from the work-order page, the Auftrag
 * number itself from the board. Either navigation abandons the form, so both go through
 * `DiscardChangesDialog` once anything has been typed.
 */

/**
 * A slot chosen somewhere else — today the disposition board.
 *
 * Kept as an alias so the name still reads at the call sites; the shape is the shared
 * `ScheduleSlot`, which is also what the board draws and what `onSlotChange` reports back.
 */
export type UpsertDefaultSlot = ScheduleSlot;

interface Props {
  /** Supplied by pages that already have work-order context; locks the field. */
  workOrderId?: string;
  /** Prefills `tradeCode` on create — e.g. `workOrder.external.gewerkId`. */
  defaultTradeCode?: number;
  /**
   * Handwerker and Termin from outside the form. **Controlled, not just a default**: it seeds the
   * form when the modal opens — over the create defaults *and* over the loaded appointment, because
   * an edit reached from the board is a reschedule and the picked slot has to win — and every later
   * change to it is written into the five scheduling fields while the modal stays open. That is the
   * board→form half of the live preview: dragging the block on the calendar retypes the form.
   *
   * Only fields whose value actually differs are written, so an echo of the form's own edit is a
   * no-op and everything else the user has typed is left alone. The durations are **required**: a
   * key present but `undefined` would blank a form default through the `reset` spread below.
   * Mirrors `features/task-upsert`.
   */
  defaultSlot?: UpsertDefaultSlot;
  /**
   * The form→board half: called with the five scheduling fields whenever they change, and with
   * `null` while the modal is closed.
   *
   * Never called with `null` for a half-typed field — an emptied Anfahrt or an unparseable date
   * simply stops reporting, so the calendar keeps drawing the last complete position instead of
   * making the block flicker out between two keystrokes.
   */
  onSlotChange?: (slot: ScheduleSlot | null) => void;
  /**
   * Ganztägig, reported **beside** the slot rather than inside it, and `false` while the modal is
   * closed.
   *
   * It cannot ride on `ScheduleSlot`: everything in that shape is compared by `sameSlot`, which
   * gates the board→form write. This flag flips a render before the durations `useFullDaySync`
   * derives from it, so on the slot it made the first publish differ while still carrying the old
   * durations — the board latched those and wrote them back over the derivation, on a loop. On its
   * own channel a stale frame is harmless, because nothing downstream writes back from it.
   *
   * The board uses it for one thing: an all-day block spans the whole shift and therefore always
   * crosses the Pause, which the API does not count as a conflict.
   */
  onFullDayChange?: (isFullDay: boolean) => void;
  /** `side` docks the form beside the calendar and leaves it usable. Default: a centred dialog. */
  placement?: "center" | "side";
  onSuccess?: (appointment: Appointment) => void;
}

const FormSkeleton: FC = () => (
  <div className="flex w-full animate-pulse flex-col gap-6">
    {[0, 1, 2].map((section) => (
      <div key={section} className="flex flex-col gap-4">
        <div className="h-4 w-28 rounded bg-slate-200" />
        <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
          <div className="h-[60px] rounded-[18px] bg-slate-100" />
          <div className="h-[60px] rounded-[18px] bg-slate-100" />
        </div>
      </div>
    ))}
  </div>
);

export const UpsertAppointmentModal: FC<Props> = ({
  workOrderId,
  defaultTradeCode = 0,
  defaultSlot,
  onSlotChange,
  onFullDayChange,
  placement = "center",
  onSuccess,
}) => {
  const {
    isOpen,
    mode,
    appointmentId,
    workOrderId: workOrderIdFromUrl,
    close,
  } = useUpsertAppointment();

  const navigate = useNavigate();

  const effectiveWorkOrderId = workOrderId ?? workOrderIdFromUrl ?? "";
  const isEdit = mode === "edit";

  const {
    data: detail,
    isFetching: isDetailFetching,
    isError: isDetailError,
  } = useAppointmentDetail(isEdit && appointmentId != null ? appointmentId : undefined);

  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  const isPending = createAppointment.isPending || updateAppointment.isPending;

  const [files, setFiles] = useState<File[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const conflictFlow = useScheduleConflictFlow();
  const { reset: resetConflicts } = conflictFlow;

  const { control, handleSubmit, reset, watch, setValue, getValues, formState } =
    useForm<AppointmentFormValues>({
      // Input and output types diverge (z.coerce on the durations), which zodResolver's generics
      // cannot express. Same cast as features/task-upsert.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      resolver: zodResolver(appointmentFormSchema) as any,
      defaultValues: getDefaultValues(effectiveWorkOrderId, defaultTradeCode),
    });

  /**
   * The seeding slot, read through a ref rather than from the closure.
   *
   * `defaultSlot` changes on every drag of the block on the calendar. In the dependency array below
   * that would re-`reset` the whole form mid-edit — wiping Beschreibung, Anhänge and the Serie
   * settings, and, once a typed date has moved the board to another day, doing it in a loop. The
   * reset must only run on the events it is about (opening, and the detail arriving), so the live
   * value is delivered here and applied by the effect after it.
   *
   * `useLayoutEffect`: layout effects run before passive ones in the same commit, so the ref is
   * already current when the reset below reads it.
   */
  const slotRef = useRef(defaultSlot);
  useLayoutEffect(() => {
    slotRef.current = defaultSlot;
  }, [defaultSlot]);

  /**
   * What the form currently holds — `edit:42` or `create` — and therefore whether it may be filled
   * again. State rather than a ref: the skeleton below is what it decides.
   *
   * **Seed exactly once per opening, and only from a settled query.** `detail` is a React Query
   * object, and a query can arrive *twice*: every reschedule invalidates `appointmentKeys.all`, so
   * the next open is served the stale cached appointment first — with `isPending` false, hence no
   * skeleton — and the fresh one a moment later. With `detail` in a dependency array that is two
   * resets with two different times, and since this form publishes what it holds to the calendar
   * (`onSlotChange`), the block visibly jumped to the previous slot and back. A query that can
   * arrive twice is a second writer of the value the board draws; this is what makes the form the
   * only one.
   *
   * The same guard stops a later background refetch — a window focus is enough — from overwriting
   * fields the user has already typed into.
   */
  const seedKey = isEdit
    ? appointmentId != null
      ? `edit:${appointmentId}`
      : null
    : // Create carries its inputs in the key rather than seeding once and for all: the work-order
      // page resolves `defaultTradeCode` from its own query, so on a deep link it can arrive after
      // the form is already on screen, and it still has to reach the Gewerk field. Create has no
      // query of its own, so there is no second arrival to guard against here.
      `create:${effectiveWorkOrderId}:${defaultTradeCode}`;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  const isSeeded = seededFor != null && seededFor === seedKey;

  // The Modal stays mounted and only toggles a CSS class, so without this the form would still
  // hold the previously edited appointment when it reopens.
  useEffect(() => {
    // Cleared on the way *out*, not only on the way in. The fields keep the closed form's values
    // until the next seeding, and a `seededFor` that outlived the closing would let them be
    // published to the calendar for the frame before the reopen re-seeds — the same stale value
    // this whole guard exists to keep off the board, and one that would then win over the fresh
    // appointment through the `slotRef` spread below.
    if (!isOpen) {
      setSeededFor(null);
      return;
    }
    setFiles([]);
    setSubmitError(null);
    setPendingExit(null);
    resetConflicts();
    setSeededFor(null);
  }, [isOpen, resetConflicts]);

  useEffect(() => {
    if (!isOpen || isSeeded || seedKey == null) return;

    if (isEdit) {
      // Still in flight — including a background refetch over a stale cache, which is exactly the
      // case that used to seed the previous time. `FormSkeleton` covers the wait.
      if (isDetailFetching) return;
      // A failed query is settled too: `retry: false` means it will not come back on its own, and
      // leaving the skeleton up forever would be worse than the empty form it falls through to.
      if (!detail && !isDetailError) return;

      // `applySlot`, not a spread: the slot's keys are the *planned* field names, and on a completed
      // appointment the picked position belongs on the Ist-Zeiten — the ones the calendar draws.
      if (detail) reset(applySlot(fromAppointment(detail), slotRef.current));
      setSeededFor(seedKey);
      return;
    }

    reset({
      ...getDefaultValues(effectiveWorkOrderId, defaultTradeCode),
      ...slotRef.current,
    });
    setSeededFor(seedKey);
  }, [
    isOpen,
    isSeeded,
    seedKey,
    isEdit,
    detail,
    isDetailFetching,
    isDetailError,
    effectiveWorkOrderId,
    defaultTradeCode,
    reset,
  ]);

  /**
   * board → form. Everything the reset above does not cover: a slot that changes *while* the modal
   * is open, i.e. the block being dragged on the calendar.
   *
   * Field by field and only where the value differs, never `reset`: the rest of the form is the
   * user's. `sameSlot` first, so the board handing back the slot this form just reported costs one
   * comparison and stops there — that is what keeps the two-way sync from looping.
   *
   * Held back until the form is seeded: the reset carries the slot itself, and writing the fields
   * before it would only be overwritten a moment later.
   *
   * **Which four fields** is the status's call, exactly as in `toSlot`: a completed appointment is
   * drawn from its Ist-Zeiten, so that is what a drag on the board retypes. Writing the planned ones
   * instead would move a block nobody can see and leave the drawn one behind. The status is read off
   * `getValues` rather than watched, so a status change does not re-run this effect — the flip is
   * the next effect's business.
   */
  useEffect(() => {
    if (!isOpen || !defaultSlot || !isSeeded) return;
    const values = getValues();
    if (sameSlot(toSlot(values), defaultSlot)) return;

    const options = { shouldDirty: true } as const;
    setValue("assignedTechnicianId", defaultSlot.assignedTechnicianId, options);

    if (hasActuals(values.status)) {
      setValue("actualStart", defaultSlot.plannedStart, options);
      setValue("actualTravelDuration", defaultSlot.plannedTravelDuration, options);
      setValue("actualWorkDuration", defaultSlot.plannedWorkDuration, options);
      setValue("actualDepartureDuration", defaultSlot.plannedDepartureDuration, options);
      return;
    }

    setValue("plannedStart", defaultSlot.plannedStart, options);
    setValue("plannedTravelDuration", defaultSlot.plannedTravelDuration, options);
    setValue("plannedWorkDuration", defaultSlot.plannedWorkDuration, options);
    setValue("plannedDepartureDuration", defaultSlot.plannedDepartureDuration, options);
  }, [isOpen, isSeeded, defaultSlot, getValues, setValue]);

  /**
   * form → board. The other half: what the five scheduling fields currently say, on every keystroke.
   * `isFullDay` rides along because `useFullDaySync` below is driven by the same values — but it is
   * reported to the board on its own channel, never inside the slot. See `onFullDayChange`.
   *
   * `useWatch` on exactly these rather than `watch()` — the whole form would re-render this
   * component on every character typed into Beschreibung.
   */
  const [
    watchedTechnicianId,
    watchedStart,
    watchedTravel,
    watchedWork,
    watchedDeparture,
    watchedIsFullDay,
    watchedStatus,
    watchedActualStart,
    watchedActualTravel,
    watchedActualWork,
    watchedActualDeparture,
    watchedActualEnd,
  ] = useWatch({
    control,
    name: [
      "assignedTechnicianId",
      "plannedStart",
      "plannedTravelDuration",
      "plannedWorkDuration",
      "plannedDepartureDuration",
      // Not a scheduling field, but it rides along with the five: the board reads it to tell a block
      // that fills a shift from one that is merely long. See `ScheduleSlot.isFullDay`.
      "isFullDay",
      // The status picks which of the two sets below the calendar is shown, so it has to be watched
      // as closely as the values themselves — see `toSlot`.
      "status",
      "actualStart",
      "actualTravelDuration",
      "actualWorkDuration",
      "actualDepartureDuration",
      // Not part of the slot and never will be: `actualEndsAt` is outside the occupancy CHECK, so
      // it moves nothing. Watched for `useActualEndSync` alone.
      "actualEnd",
    ],
  });

  const liveSlot = useMemo(
    () =>
      toSlot({
        assignedTechnicianId: watchedTechnicianId ?? null,
        status: watchedStatus,
        plannedStart: watchedStart ?? "",
        plannedTravelDuration: watchedTravel,
        plannedWorkDuration: watchedWork,
        plannedDepartureDuration: watchedDeparture,
        actualStart: watchedActualStart ?? "",
        actualTravelDuration: watchedActualTravel,
        actualWorkDuration: watchedActualWork,
        actualDepartureDuration: watchedActualDeparture,
      }),
    [
      watchedTechnicianId,
      watchedStatus,
      watchedStart,
      watchedTravel,
      watchedWork,
      watchedDeparture,
      watchedActualStart,
      watchedActualTravel,
      watchedActualWork,
      watchedActualDeparture,
    ],
  );

  /**
   * Plan → Ist, once, when the status is switched to a completed one.
   *
   * The moment `hasActuals` turns true the form changes which four fields it publishes to the
   * calendar. Without this the block would jump to the stored Ist position — usually the *planned*
   * one, since `fromAppointment` falls back to it — and a move the user had just made by dragging
   * would appear to be thrown away. Copying the Plan values across keeps the block exactly where it
   * is, and opens the Ist-Zeiten showing what was planned: the right starting point for a
   * correction, and the same fallback `fromAppointment` already uses.
   *
   * **Adopt on the first pass, derive only on a change** — the rule `useFullDaySync` opens with. A
   * baseline of `false` would fire this on every completed appointment the moment it loads and
   * overwrite the very Ist-Zeiten the technician reported. `null` means "not yet baselined", which
   * is why the ref is cleared whenever the form is unseeded rather than just on close.
   *
   * The status is read from `getValues` and only *triggered* by the watched copy: both the baseline
   * and the comparison then come from the same authoritative store, so a `useWatch` value that lags
   * the reset by a frame delays this effect instead of reading a flip that never happened.
   */
  const wasDoneRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isOpen || !isSeeded) {
      wasDoneRef.current = null;
      return;
    }

    const values = getValues();
    const isDone = hasActuals(values.status);

    if (wasDoneRef.current === null || wasDoneRef.current === isDone) {
      wasDoneRef.current = isDone;
      return;
    }

    wasDoneRef.current = isDone;
    // Leaving a completed state changes nothing: the Plan-Zeiten were never touched while the Ist
    // ones were being edited, so the form simply goes back to publishing them.
    if (!isDone) return;

    const options = { shouldDirty: true } as const;
    setValue("actualStart", values.plannedStart, options);
    setValue("actualTravelDuration", values.plannedTravelDuration, options);
    setValue("actualWorkDuration", values.plannedWorkDuration, options);
    setValue("actualDepartureDuration", values.plannedDepartureDuration, options);
  }, [isOpen, isSeeded, watchedStatus, getValues, setValue]);

  /**
   * Ist-Ende trails the Ist times. Enabled on exactly the condition that puts the fields on screen,
   * so flipping the status on takes the baseline rather than immediately rewriting the value.
   */
  useActualEndSync({
    enabled: isOpen && isSeeded && hasActuals(watchedStatus),
    actualStart: watchedActualStart ?? "",
    actualWorkDuration: watchedActualWork,
    actualEnd: watchedActualEnd ?? "",
    setValue,
  });

  /**
   * The shift Ganztägig fills. Read from the technician's own profile rather than from the board,
   * because this modal also opens from `pages/work-order-detail`, where no calendar query runs and
   * `BoardRowModel.shift` therefore does not exist.
   */
  const { shift: technicianShift, isPending: isShiftPending } = useTechnicianShift(
    watchedTechnicianId ?? null,
    // Both modals stay mounted for the whole life of the board; only the open one needs a shift.
    { enabled: isOpen },
  );

  /**
   * `enabled` is held back until the form is seeded, for the same reason `onSlotChange` is below:
   * deriving over values the seeding `reset` is about to replace would write a Termin nobody asked
   * for, and publish it to the board on the way.
   */
  useFullDaySync({
    enabled: isOpen && isSeeded,
    isFullDay: Boolean(watchedIsFullDay),
    technicianId: watchedTechnicianId ?? null,
    plannedStart: watchedStart ?? "",
    travel: watchedTravel,
    work: watchedWork,
    departure: watchedDeparture,
    shift: technicianShift,
    setValue,
  });

  /**
   * Ganztägig is on but nothing could be derived from it — an archived Handwerker, or one whose
   * profile carries no shift. The durations are then whatever is in the fields, which is a
   * defensible outcome but a silent one, so the form says why.
   */
  const fullDayHint =
    watchedIsFullDay && watchedTechnicianId != null && !technicianShift && !isShiftPending
      ? "Für diesen Handwerker ist keine Schicht hinterlegt — die Zeiten bleiben unverändert."
      : undefined;

  useEffect(() => {
    if (!onSlotChange) return;
    // Closed: nothing is being planned, so the calendar goes back to what it drew before.
    if (!isOpen) {
      onSlotChange(null);
      return;
    }
    // Nothing is published before the form has been seeded from a settled query. This is what keeps
    // a stale cached appointment from reaching the calendar: the block is drawn from what the user
    // is going to edit, never from a value the form is about to replace.
    if (!isSeeded) return;
    // `null` here is an incomplete field, not an empty plan — say nothing and let the last complete
    // position stand.
    if (liveSlot) onSlotChange(liveSlot);
  }, [onSlotChange, isOpen, isSeeded, liveSlot]);

  /**
   * The same publish, for Ganztägig alone. Separate effect, separate prop, deliberately — see
   * `onFullDayChange`.
   *
   * Unlike the slot this is reported unconditionally rather than held back on an incomplete field:
   * there is no half-typed boolean, and the board only ever reads it to relax a warning.
   */
  useEffect(() => {
    if (!onFullDayChange) return;
    onFullDayChange(isOpen && isSeeded ? Boolean(watchedIsFullDay) : false);
  }, [onFullDayChange, isOpen, isSeeded, watchedIsFullDay]);

  // Already subscribed to above for the live slot — the recurrence preview reads the same value.
  const plannedStart = watchedStart ?? "";
  const existingAttachments = watch("attachments");

  /**
   * The Auftrag is read from the **form**, not from `effectiveWorkOrderId`: `openEdit` does not
   * clear `upsert-appointment-work-order-id`, so on the board a create followed by an edit leaves a
   * stale id in the URL. The form value is right in both modes — seeded from the prop or the URL on
   * create, taken from the loaded appointment on edit.
   *
   * Blanked until the form is seeded, because the header sits outside the `FormSkeleton` and would
   * otherwise flash the previous appointment's number.
   */
  const displayWorkOrderId = isSeeded ? watch("workOrderId") : "";

  /**
   * Where "Kalender" goes. Only offered on a page that already owns the work order — on the board
   * you are already there. Edit waits for `detail`: the reschedule link carries the Termin's own
   * day so the board opens where the Termin actually is, and that date is only in the detail.
   */
  const calendarTo = !workOrderId
    ? null
    : isEdit
      ? detail
        ? routes.disposition.rescheduleAppointment(
            detail.id,
            detail.schedule.plannedWorkStartsAt,
          )
        : null
      : routes.disposition.planAppointment(workOrderId);

  /**
   * Every way out of a half-filled form asks first.
   *
   * Two of them: the header links, which leave the page and take the form with them, and simply
   * dismissing the modal — Escape, the backdrop, the X. The second used to throw the input away in
   * silence, which is the same loss the links have always warned about.
   *
   * One piece of state rather than two booleans, because the dialog is single and has to know what
   * to do when it is confirmed.
   */
  const [pendingExit, setPendingExit] = useState<
    { kind: "navigate"; to: string } | { kind: "close" } | null
  >(null);

  /** Modifier-clicks fall through untouched — opening a new tab loses nothing. */
  const guardNav = (to: string) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    if (formState.isDirty) {
      setPendingExit({ kind: "navigate", to });
      return;
    }
    // Deliberately no `close()` first: it is a nuqs replace-state, and landing after the route
    // change it would rewrite the destination URL and strip the query string the route builder
    // just produced. Navigating to an absolute path drops the `upsert-appointment*` params anyway.
    navigate(to);
  };

  const requestClose = () => {
    if (formState.isDirty) {
      setPendingExit({ kind: "close" });
      return;
    }
    close();
  };

  const removeExistingAttachment = (index: number) =>
    setValue(
      "attachments",
      existingAttachments.filter((_, i) => i !== index),
      { shouldDirty: true },
    );

  /**
   * `values` already carries the uploaded attachments on a retry, so `alreadyUploaded` skips a
   * second round trip for the same files.
   *
   * The overrides are accumulated by `useScheduleConflictFlow` and arrive here already merged —
   * this function only forwards them to the mapper.
   */
  const submit = async (
    values: AppointmentFormValues,
    {
      overrides = {} as ScheduleOverrides,
      alreadyUploaded = false,
    }: { overrides?: ScheduleOverrides; alreadyUploaded?: boolean } = {},
  ) => {
    setSubmitError(null);
    // Declared outside the try so the retry closure below can resend the exact payload that was
    // rejected — uploads and all.
    let withUploads: AppointmentFormValues = values;
    try {
      const uploaded =
        files.length && !alreadyUploaded ? (await appointmentApi.uploadFiles(files)).data : [];
      withUploads = {
        ...values,
        attachments: [...values.attachments, ...uploaded],
      };

      const appointment = isEdit
        ? (
            await updateAppointment.mutateAsync({
              id: appointmentId as number,
              dto: toUpdateDto(withUploads, {
                hasSeries: detail?.seriesId != null,
                ...overrides,
              }),
              workOrderId: withUploads.workOrderId,
            })
          ).appointment
        : await createAppointment.mutateAsync(toCreateDto(withUploads, overrides));

      onSuccess?.(appointment);
      close();
    } catch (error) {
      const handled = conflictFlow.handle(error, {
        overrides,
        attempt: (next) => submit(withUploads, { overrides: next, alreadyUploaded: true }),
      });
      if (!handled) setSubmitError(getErrorMessage(error));
    }
  };

  const onSubmit = handleSubmit((values) => submit(values));

  /**
   * Ties the footer button to the `<form>` below through the `form` attribute — the footer is a
   * sibling of the modal body, so without it the button is outside the form and Enter has no
   * submit button to trigger. See `Modal`'s `SubmitButtonConfig.formId`.
   */
  const formId = useId();

  return (
    <>
      <Modal
        show={isOpen}
        setShow={(next) => {
          if (!next) requestClose();
        }}
        size="wide"
        placement={placement}
        title={isEdit ? "Termin bearbeiten" : "Neuer Termin"}
        topLeftChildren={
          calendarTo && (
            <Link
              to={calendarTo}
              onClick={guardNav(calendarTo)}
              title={
                isEdit ? "Termin im Kalender verschieben" : "Termin im Kalender planen"
              }
              className="flex items-center gap-1.5 text-sm text-[#124093] hover:underline"
            >
              <CalendarDays size={16} aria-hidden="true" />
              Kalender
            </Link>
          )
        }
        subtitle={
          // The work order is context, not a decision — it belongs in the header, not as a
          // disabled input at the top of the form. A link only away from its own page: on
          // `/work-orders/:id` it would point at the page you are already looking at.
          displayWorkOrderId ? (
            workOrderId ? (
              `Auftrag Nr. ${displayWorkOrderId}`
            ) : (
              <Link
                to={routes.task.detail(displayWorkOrderId)}
                onClick={guardNav(routes.task.detail(displayWorkOrderId))}
                className="underline underline-offset-2 hover:text-slate-700"
              >
                Auftrag Nr. {displayWorkOrderId}
              </Link>
            )
          ) : undefined
        }
        submitButton={{
          formId,
          title: isEdit ? "Speichern" : "Termin anlegen",
          isPending,
          // The `<form>` does not exist yet behind the skeleton, so `form={formId}` would point at
          // nothing and the button would silently do nothing.
          disabled: isPending || (isEdit && !isSeeded),
        }}
        footerMessage={
          submitError && (
            <p className="text-sm text-red-500" role="alert">
              {submitError}
            </p>
          )
        }
      >
        {isEdit && !isSeeded ? (
          <FormSkeleton />
        ) : (
          // A real submit handler, not `preventDefault`. This is what makes Enter in any
          // single-line field save the Termin: the form has a submit button — the one in the
          // footer, adopted through `form={formId}` — so the browser's implicit submission applies.
          // `Beschreibung` still takes newlines, and the comboboxes swallow Enter while their list
          // is open, both by spec rather than by special-casing here.
          <div className="flex w-full flex-col gap-6">
            <form id={formId} onSubmit={onSubmit} noValidate className="w-full">
              <fieldset disabled={isPending} className="w-full border-0 p-0 m-0">
                <AppointmentFormFields
                  control={control}
                  mode={isEdit ? "edit" : "create"}
                  workOrderLocked={Boolean(displayWorkOrderId)}
                  hasSeries={detail?.seriesId != null}
                  plannedStart={plannedStart}
                  fullDayHint={fullDayHint}
                  files={files}
                  onFilesChange={setFiles}
                  existingAttachments={existingAttachments}
                  onRemoveExistingAttachment={removeExistingAttachment}
                />
              </fieldset>
            </form>
            {/* Outside the <form>: read-only, and not the office's to edit here — the worker owns
                these. Renders nothing until the technician has sent something. */}
            {isEdit && detail && (
              <TechnicianFeedbackSummary
                comment={detail.technicianComment}
                attachments={detail.technicianAttachments}
              />
            )}
          </div>
        )}
      </Modal>

      {/* Siblings of the Modal, not children: every modal shares z-index 100, so the one later in
        the DOM wins. */}
      <DiscardChangesDialog
        open={pendingExit != null}
        message={
          pendingExit?.kind === "close"
            ? "Ihre Eingaben in diesem Termin gehen verloren, wenn Sie ihn jetzt schließen."
            : "Ihre Eingaben in diesem Termin gehen verloren, wenn Sie die Seite jetzt verlassen."
        }
        onConfirm={() => {
          const exit = pendingExit;
          setPendingExit(null);
          if (exit?.kind === "navigate") navigate(exit.to);
          else if (exit?.kind === "close") close();
        }}
        onCancel={() => setPendingExit(null)}
      />

      <ScheduleConflictDialogs
        flow={conflictFlow}
        isLoading={isPending}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
        conflictTitle="Terminüberschneidung erkannt"
        conflictDescription={
          isEdit
            ? "Der gewählte Zeitraum ist bereits belegt. Möchten Sie den Termin trotzdem speichern? Die betroffenen Einträge werden zur Umplanung markiert."
            : "Der gewählte Zeitraum ist bereits belegt. Möchten Sie den Termin trotzdem anlegen? Die betroffenen Einträge werden zur Umplanung markiert."
        }
        conflictConfirmLabel={isEdit ? "Trotzdem speichern" : "Trotzdem anlegen"}
        workingHoursDescription={
          isEdit
            ? "Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie den Termin trotzdem speichern?"
            : "Der Techniker arbeitet zu dieser Zeit nicht. Möchten Sie den Termin trotzdem anlegen?"
        }
        blockersDescription="Die folgenden abgeschlossenen Einträge blockieren diesen Zeitraum. Bitte planen Sie diese zuerst um, bevor Sie den Termin speichern."
      />
    </>
  );
};
